import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { AuthShell } from '@/components/auth/auth-shell';
import { ForgotPasswordHero } from '@/components/auth/forgot-password-hero';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { requestPasswordReset } from '@/services/api.client';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
});

type FormValues = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  // Always 204 — success banner is shown regardless of whether the address is
  // registered (anti-enumeration per SR-08). Form stays visible so the user
  // can see their submitted email alongside the confirmation message.
  const mutation = useMutation({
    mutationFn: ({ email }: FormValues) => requestPasswordReset(email),
  });

  const onSubmit = (values: FormValues) => {
    mutation.mutate(values);
  };

  return (
    <AuthShell
      hero={<ForgotPasswordHero />}
      title="Forgot Password"
      description="Enter your account email address and we'll send you a secure link to reset your password."
    >
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="email">Email Address</FieldLabel>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              disabled={mutation.isPending || mutation.isSuccess}
              aria-invalid={!!errors.email}
              {...register('email')}
            />
            {errors.email && (
              <p className="text-sm text-destructive" role="alert">
                {errors.email.message}
              </p>
            )}
          </Field>

          {/* Inline success banner — shown after submit, form stays visible */}
          {mutation.isSuccess && (
            <div
              role="status"
              className="flex items-start gap-3 rounded-lg border p-4 text-sm"
              style={{
                borderColor: 'var(--status-green)',
                backgroundColor: 'var(--status-green-tint)',
                color: 'var(--text-success)',
              }}
            >
              If your email is registered, a reset link will be sent within 60 seconds. Check your
              spam folder if it doesn&apos;t arrive.
            </div>
          )}

          <Field>
            <Button type="submit" disabled={mutation.isPending || mutation.isSuccess}>
              {mutation.isPending
                ? 'Sending…'
                : mutation.isSuccess
                  ? 'Link sent'
                  : 'Send Reset Link'}
            </Button>
          </Field>
          <p className="text-center text-sm text-[var(--text-secondary)]">
            <Link
              to="/login"
              className="underline underline-offset-4 hover:text-[var(--text-accent)]"
            >
              ← Back to Sign In
            </Link>
          </p>
        </FieldGroup>
      </form>
    </AuthShell>
  );
}
